import { faFileImport, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Checkbox, FormControlLabel, LinearProgress, Typography, Alert } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPhase, RunProgress } from 'views/imports/types';

type ProgressView = { caption: string | null; percent: number | null };

const toStep = (progress: RunProgress) => ({
  current: Math.min(progress.current + 1, progress.total),
  total: progress.total,
});

const toPercent = (progress: RunProgress): number => (
  progress.total > 0 ? (progress.current / progress.total) * 100 : 0
);

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  cluster: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  progress: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  caption: {
    color: theme.palette.text.secondary,
  },
  hint: {
    color: theme.palette.text.secondary,
    textAlign: 'left',
  },
  checkbox: theme.palette.mode === 'dark' ? {
    '&&.Mui-checked': { color: theme.palette.text.primary },
  } : {},
}));

type ExtractControlsProps = {
  phase: ImportPhase;
  progress: RunProgress | null;
  canExtract: boolean;
  serviceReady: boolean;
  hasPages: boolean;
  onExtract: () => void;
  cancelling?: boolean;
  /** Once results exist, Send to assistant is the primary action and Extract steps back. */
  extractIsSecondary?: boolean;
  onCancel: () => void;
  onClear: () => void;
  selectionHint?: boolean;
  reviewEnabled: boolean;
  onToggleReview: (enabled: boolean) => void;
  reviewFailedCount: number;
  onRetryReview: () => void;
  scanTables: boolean;
  onToggleScanTables: (enabled: boolean) => void;
};

export const ExtractControls = ({
  phase,
  progress,
  canExtract,
  serviceReady,
  hasPages,
  onExtract,
  cancelling = false,
  extractIsSecondary = false,
  onCancel,
  onClear,
  selectionHint = false,
  reviewEnabled,
  onToggleReview,
  reviewFailedCount,
  onRetryReview,
  scanTables,
  onToggleScanTables,
}: ExtractControlsProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const isBusy = phase !== 'idle';
  // Only phases that can be interrupted offer Cancel, and only until a cancel has been requested.
  const isCancellablePhase = phase === 'ocr' || phase === 'extracting' || phase === 'reviewing';
  const canCancel = isCancellablePhase && !cancelling;
  const canClear = !isBusy && hasPages;
  const buttonHeight = theme.spacing(5.5);
  const disabledText = theme.palette.text.disabled;
  const destructiveText = theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark;

  const runningView = (key: string): ProgressView => (progress
    ? { caption: t(key, toStep(progress)), percent: toPercent(progress) }
    : { caption: null, percent: null });

  const resolveProgressView = (): ProgressView | null => {
    switch (phase) {
      case 'idle':
        return null;
      case 'converting':
        return { caption: t('imports:dropzone.converting'), percent: null };
      case 'ocr':
        return runningView('imports:extract.ocrProgress');
      case 'extracting':
        return runningView('imports:extract.progress');
      case 'reviewing':
        return runningView('imports:extract.reviewProgress');
      default: {
        const unreachable: never = phase;
        return unreachable;
      }
    }
  };

  const view = cancelling && isBusy
    ? { caption: t('imports:extract.cancelling'), percent: resolveProgressView()?.percent ?? null }
    : resolveProgressView();

  return (
    <div className={classes.root}>
      {!serviceReady && (
        <Alert severity="info">{t('imports:extract.serviceNotReady')}</Alert>
      )}
      <div className={classes.actions}>
        <BaseButton
          text={t('imports:extract.clear')}
          icon={faTrashAlt}
          buttonVariant="text"
          buttonColor="inherit"
          textColor={canClear ? theme.palette.error.dark : disabledText}
          height={buttonHeight}
          disabled={!canClear}
          onClick={onClear}
        />
        <div className={classes.cluster}>
          <BaseButton
            text={t('imports:extract.cancel')}
            icon={faTimes}
            buttonVariant="outlined"
            buttonColor="inherit"
            textColor={canCancel ? theme.palette.text.primary : disabledText}
            height={buttonHeight}
            disabled={!canCancel}
            onClick={onCancel}
          />
          <BaseButton
            text={t('imports:extract.button')}
            icon={faFileImport}
            buttonVariant={extractIsSecondary ? 'outlined' : undefined}
            buttonColor={extractIsSecondary ? 'inherit' : undefined}
            textColor={canExtract || isBusy ? theme.palette.primary.main : disabledText}
            height={buttonHeight}
            isLoading={isBusy}
            disabled={!canExtract || isBusy}
            onClick={onExtract}
          />
        </div>
      </div>
      <FormControlLabel
        control={(
          <Checkbox
            color="primary"
            className={classes.checkbox}
            checked={reviewEnabled}
            disabled={isBusy}
            onChange={(event) => onToggleReview(event.target.checked)}
          />
        )}
        label={(
          <Typography variant="body2">{t('imports:extract.reviewLabel')}</Typography>
        )}
      />
      <Typography variant="caption" className={classes.hint}>
        {t('imports:extract.reviewHint')}
      </Typography>
      <FormControlLabel
        control={(
          <Checkbox
            color="primary"
            className={classes.checkbox}
            checked={scanTables}
            disabled={isBusy}
            onChange={(event) => onToggleScanTables(event.target.checked)}
          />
        )}
        label={(
          <Typography variant="body2">{t('imports:extract.scanTablesLabel')}</Typography>
        )}
      />
      <Typography variant="caption" className={classes.hint}>
        {t('imports:extract.scanTablesHint')}
      </Typography>
      {reviewFailedCount > 0 && (
        <BaseButton
          text={t('imports:extract.retryReview', { count: reviewFailedCount })}
          buttonVariant="outlined"
          buttonColor="inherit"
          textColor={isBusy || !serviceReady ? disabledText : theme.palette.primary.main}
          height={buttonHeight}
          disabled={isBusy || !serviceReady}
          onClick={onRetryReview}
        />
      )}
      {selectionHint && !isBusy && (
        <Typography variant="caption" className={classes.hint}>
          {t('imports:extract.noSelection')}
        </Typography>
      )}
      {view && (
        <div className={classes.progress}>
          {view.percent === null ? (
            <LinearProgress aria-label={view.caption ?? t('imports:dropzone.converting')} />
          ) : (
            <LinearProgress
              variant="determinate"
              value={view.percent}
              aria-label={view.caption ?? undefined}
            />
          )}
          {view.caption && (
            <Typography variant="caption" role="status" aria-live="polite" className={classes.caption}>
              {view.caption}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};
